import React, { memo, forwardRef, useImperativeHandle, useRef, useMemo, useState } from 'react';
import { Button, Form, Input } from 'antd';
import { Formik, FormikProps } from 'formik';
import * as yup from 'yup';
import useSWR from 'swr';
import { GeneralApi, PermissionApi } from '../../apis';
import styled from 'styled-components';
import { PermissionTable, Permission } from './table';

const validationSchema = yup.object().shape({
  name: yup.string().required('Заавал оруулна'),
  rolePermissions: yup.array().required('Заавал оруулна'),
});

export type Role = Permission;

type FormValues = {
  name: string | undefined;
  rolePermissions: Role[] | undefined;
};

type Props = {
  onSubmit: (values: any) => void;
  action: any;
};

export type BrandFormRef = {
  submitForm: () => any;
  getValues: () => any;
};

const PermissionForm = memo(
  forwardRef<BrandFormRef, Props>(({ onSubmit: _onSubmit, action }, ref) => {
    const formikRef = useRef<FormikProps<FormValues>>(null);
    const { data: init } = useSWR('/api/init', GeneralApi.init);

    useImperativeHandle(ref, () => ({
      submitForm: async () => {
        const validate = await formikRef.current?.submitForm();
        return validate;
      },
      getValues: () => {},
    }));

    const initialValues = useMemo(() => {
      return {
        name: '',
        rolePermissions: [],
        ...(action && action.payload),
      };
    }, [action]);

    return (
      <Container>
        <Formik
          key={action?.payload?.id || 'new-role'}
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={_onSubmit}
          innerRef={formikRef}
        >
          {({ handleChange, values, errors, touched, handleSubmit, isSubmitting }) => {
            return (
              <>
                <Form layout="vertical">
                  <div className="card">
                    <div className="inputContainer">
                      <Form.Item
                        label="Нэр"
                        layout="vertical"
                        validateStatus={errors.name && touched.name ? 'error' : ''}
                        help={errors.name && touched.name ? errors.name : ('' as any)}
                      >
                        <Input
                          name="name"
                          value={values.name}
                          onChange={handleChange}
                          placeholder="Нэр"
                          status={touched.name && errors.name ? 'error' : ''}
                        />
                      </Form.Item>
                    </div>

                    <div className="inputContainer">
                      <Form.Item
                        label="Хандах цэс"
                        layout="vertical"
                        validateStatus={
                          errors.rolePermissions && touched.rolePermissions ? 'error' : ''
                        }
                        help={
                          errors.rolePermissions && touched.rolePermissions
                            ? errors.rolePermissions
                            : ('' as any)
                        }
                      >
                        <PermissionTable
                          data={init?.permissions || []}
                        />
                      </Form.Item>
                    </div>
                  </div>
                  <div className="h-[20px]" />

                  <div className="flex flex-row justify-end">
                    <Button type="primary" loading={isSubmitting} onClick={() => handleSubmit()}>
                      Хадгалах
                    </Button>
                  </div>
                </Form>
              </>
            );
          }}
        </Formik>
      </Container>
    );
  })
);

const Container = styled.div`
  .perm-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 8px;
  }

  .perm-grid .ant-checkbox-wrapper {
    margin-inline-start: 0 !important;
    margin-inline-end: 0 !important;
  }

  .perm-grid .perm-cell {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 10px;
    border-radius: 6px;
    box-shadow: inset 0 0 0 1px #e5e7eb;
    background: #fff;
    padding-top: 10px;
  }
`;

PermissionForm.displayName = 'PermissionForm';

export { PermissionForm };
